import React from 'react';
import { Link } from 'react-router-dom';
import { Code, Palette, TrendingUp, Smartphone, Database, Shield, ArrowRight, Check } from 'lucide-react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import AdSlot from '../components/AdSlot';

const Services: React.FC = () => {
  const services = [
    {
      icon: Code,
      title: 'Custom Web Development',
      description: 'We build powerful, scalable websites tailored to your business needs using modern technologies.',
      features: [
        'Custom website development',
        'E-commerce solutions',
        'Content Management Systems',
        'API integrations',
        'Performance optimization',
      ],
      pricing: 'Starting at $2,999',
    },
    {
      icon: Palette,
      title: 'Website Design',
      description: 'Beautiful, user-friendly designs that engage visitors and drive conversions.',
      features: [
        'UI/UX design',
        'Responsive layouts',
        'Brand identity',
        'Wireframing & prototyping',
        'Design systems',
      ],
      pricing: 'Starting at $1,499',
    },
    {
      icon: TrendingUp,
      title: 'Digital Marketing',
      description: 'Comprehensive marketing strategies to grow your online presence and reach your target audience.',
      features: [
        'SEO optimization',
        'Content marketing',
        'Social media management',
        'Email campaigns',
        'Analytics & reporting',
      ],
      pricing: 'Starting at $999/month',
    },
    {
      icon: Smartphone,
      title: 'Mobile Optimization',
      description: 'Ensure your website looks perfect and performs flawlessly on all mobile devices.',
      features: [
        'Mobile-first design',
        'Speed optimization',
        'Touch-friendly interfaces',
        'Cross-device testing',
        'Progressive Web Apps',
      ],
      pricing: 'Starting at $799',
    },
    {
      icon: Database,
      title: 'Maintenance & Support',
      description: 'Keep your website running smoothly with our ongoing maintenance and support services.',
      features: [
        'Regular updates',
        'Security monitoring',
        'Backup management',
        'Bug fixes',
        '24/7 support',
      ],
      pricing: 'Starting at $299/month',
    },
    {
      icon: Shield,
      title: 'Website Security',
      description: 'Protect your website and customer data with comprehensive security solutions.',
      features: [
        'SSL certificates',
        'Malware scanning',
        'DDoS protection',
        'Security audits',
        'Compliance consulting',
      ],
      pricing: 'Starting at $499',
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery',
      description: 'We learn about your business, goals, and target audience.',
    },
    {
      step: '02',
      title: 'Planning',
      description: 'We create a detailed project plan and timeline.',
    },
    {
      step: '03',
      title: 'Design',
      description: 'We design beautiful mockups for your approval.',
    },
    {
      step: '04',
      title: 'Development',
      description: 'We build your website using best practices.',
    },
    {
      step: '05',
      title: 'Testing',
      description: 'We thoroughly test everything before launch.',
    },
    {
      step: '06',
      title: 'Launch',
      description: 'We deploy your website and provide training.',
    },
  ];

  return (
    <>
      <SEO
        title="Our Services"
        description="Professional web development, design, and digital marketing services. Transform your online presence with TheCodeBazaar."
        schemaType="page"
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-sky-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Professional Web Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            From design to development, marketing to maintenance - we provide everything you need to succeed online.
          </p>
          <Link to="/hire">
            <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
              Get Started Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What We Offer</h2>
            <p className="text-lg text-gray-600">Comprehensive solutions for all your web needs</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-sky-100 to-blue-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="w-8 h-8 text-sky-500" />
                </div>

                <h3 className="text-2xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>

                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="hidden pt-6 border-t border-gray-200">
                  <div className="text-2xl font-bold text-sky-500 mb-4">{service.pricing}</div>
                  <Link to="/hire">
                    <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:shadow-lg transition-all">
                      Learn More
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AdSlot slot="heroBanner" />
      {/* Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Process</h2>
            <p className="text-lg text-gray-600">How we bring your vision to life</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div className="text-5xl font-bold text-sky-100 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-sky-500 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-sky-50">
            Let's discuss your project and create something amazing together
          </p>
          <Link to="/hire">
            <Button className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
};

export default Services;